import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import AppLayout, { AccessDenied } from "@/components/AppLayout";

import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import SpkInbox from "@/pages/SpkInbox";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import PartMasuk from "@/pages/PartMasuk";
import PartPesan from "@/pages/PartPesan";
import Warehouse from "@/pages/Warehouse";
import PartKomplit from "@/pages/PartKomplit";
import PartOverdue from "@/pages/PartOverdue";
import Salvage from "@/pages/Salvage";
import PartRetur from "@/pages/PartRetur";
import PartKeluar from "@/pages/PartKeluar";
import PartTerpasang from "@/pages/PartTerpasang";
import ReceivingHistory from "@/pages/ReceivingHistory";
import ProcurementDetail from "@/pages/ProcurementDetail";
import AIImport from "@/pages/AIImport";
import History from "@/pages/History";
import Unmatched from "@/pages/Unmatched";
import Reports from "@/pages/Reports";
import AuditLog from "@/pages/AuditLog";
import UsersPage from "@/pages/UsersPage";
import SettingsPage from "@/pages/SettingsPage";
import SearchResults from "@/pages/SearchResults";
import Notifications from "@/pages/Notifications";

function Loading() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F8F9FA]">
      <div className="animate-pulse text-gray-400 font-head tracking-wide">TITANIUM PARTS…</div>
    </div>
  );
}

function Protected({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (user === false) return <Navigate to="/login" replace />;
  return children;
}

function Guard({ roles, children }) {
  const { user } = useAuth();
  if (!roles.includes(user.role)) return <AccessDenied />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route
        element={
          <Protected>
            <AppLayout />
          </Protected>
        }
      >
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/spk" element={<Guard roles={["owner", "partman"]}><SpkInbox /></Guard>} />
        <Route path="/jobs" element={<Guard roles={["owner", "partman", "viewer"]}><Jobs /></Guard>} />
        <Route path="/jobs/:id" element={<JobDetail />} />
        <Route path="/part-masuk" element={<Guard roles={["owner", "warehouse_receiving", "security_receiving", "warehouse_out"]}><PartMasuk /></Guard>} />
        <Route path="/part-pesan/:jobId" element={<Guard roles={["owner", "partman"]}><ProcurementDetail /></Guard>} />
        <Route path="/part-pesan" element={<Guard roles={["owner", "partman"]}><PartPesan /></Guard>} />
        <Route path="/warehouse" element={<Guard roles={["owner", "partman", "warehouse_receiving", "warehouse_out", "viewer"]}><Warehouse /></Guard>} />
        <Route path="/wh-stock" element={<Guard roles={["owner", "partman", "warehouse_receiving", "warehouse_out", "viewer"]}><Warehouse /></Guard>} />
        <Route path="/part-komplit" element={<Guard roles={["owner", "partman", "viewer"]}><PartKomplit /></Guard>} />
        <Route path="/part-overdue" element={<Guard roles={["owner", "partman", "viewer"]}><PartOverdue /></Guard>} />
        <Route path="/salvage" element={<Guard roles={["owner", "partman", "warehouse_out", "viewer"]}><Salvage /></Guard>} />
        <Route path="/part-retur" element={<Guard roles={["owner", "warehouse_out"]}><PartRetur /></Guard>} />
        <Route path="/part-keluar" element={<Guard roles={["owner", "warehouse_out"]}><PartKeluar /></Guard>} />
        <Route path="/part-terpasang" element={<Guard roles={["owner", "warehouse_out"]}><PartTerpasang /></Guard>} />
        <Route path="/receiving-history" element={<Guard roles={["owner", "warehouse_receiving", "security_receiving", "warehouse_out"]}><ReceivingHistory /></Guard>} />
        <Route path="/import" element={<Guard roles={["owner"]}><AIImport /></Guard>} />
        <Route path="/unmatched" element={<Guard roles={["owner", "partman", "warehouse_receiving", "security_receiving"]}><Unmatched /></Guard>} />
        <Route path="/reports" element={<Guard roles={["owner", "partman", "warehouse_receiving", "warehouse_out", "viewer"]}><Reports /></Guard>} />
        <Route path="/audit" element={<Guard roles={["owner", "viewer"]}><AuditLog /></Guard>} />
        <Route path="/history" element={<Guard roles={["owner", "partman", "warehouse_receiving", "warehouse_out", "viewer"]}><History /></Guard>} />
        <Route path="/users" element={<Guard roles={["owner"]}><UsersPage /></Guard>} />
        <Route path="/settings" element={<Guard roles={["owner"]}><SettingsPage /></Guard>} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/notifications" element={<Notifications />} />
      </Route>
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
